import React from 'react';
import { Alert, Button, DatePicker, Form, Input, Modal, Spin, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { DOCUMENT_UI_DATETIME_FORMAT } from '../../../../utils/documentUiFormat';
import { CLOSING_PACKAGE_MODAL_TITLES } from '../../workspace/closingPackageModalKinds';
import { useSendScansModal } from '../../hooks/useSendScansModal';
import ClosingPackageScanDocumentsTable from './ClosingPackageScanDocumentsTable';
import ClosingPackageTaskModalContext from './ClosingPackageTaskModalContext';
import ClosingPackageTaskModalEventDateHint from './ClosingPackageTaskModalEventDateHint';
import ClosingPackageTaskModalFooter from './ClosingPackageTaskModalFooter';
import type { ClosingPackageTaskModalBaseProps } from './ClosingPackageFormationModal.types';

const { Text } = Typography;

export type ClosingPackageSendScansModalProps = ClosingPackageTaskModalBaseProps & {
  onSuccess?: () => void | Promise<void>;
};

/**
 * SEND_SCANS_TO_CLIENT — prepare scan package for external delivery (DOC-013B).
 */
const ClosingPackageSendScansModal: React.FC<ClosingPackageSendScansModalProps> = ({
  open,
  onClose,
  applicationId,
  onSuccess,
}) => {
  const {
    form,
    projection,
    contextView,
    contextLoading,
    submitting,
    archiveDownloaded,
    archiveDownloading,
    archiveEntryCount,
    handleDownloadArchive,
    handleSubmit,
    canSubmit,
  } = useSendScansModal({
    applicationId,
    open,
    onClose,
    onSuccess,
  });

  return (
    <Modal
      title={CLOSING_PACKAGE_MODAL_TITLES['send-scans']}
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={980}
      data-testid="send-scans-modal"
      footer={
        <div>
          {!canSubmit ? (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Для подтверждения отправки необходимо скачать архив документов.
            </Text>
          ) : null}
          <ClosingPackageTaskModalFooter
            onClose={onClose}
            onSubmit={handleSubmit}
            submitting={submitting}
            submitLabel="Отправить сканы"
            submitTestId="send-scans-submit"
            submitDisabled={!canSubmit}
          />
        </div>
      }
    >
      {contextLoading || !projection ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <>
          <ClosingPackageTaskModalContext contextView={contextView} />

          <Form form={form} layout="vertical">
            <Form.Item
              name="dispatchedAt"
              label="Дата отправки сканов"
              rules={[{ required: true, message: 'Укажите дату отправки сканов' }]}
            >
              <DatePicker showTime style={{ width: '100%' }} format={DOCUMENT_UI_DATETIME_FORMAT} />
            </Form.Item>

            <Form.Item name="recipient" label="Получатель">
              <Input
                placeholder="E-mail или ФИО получателя (необязательно)"
                data-testid="send-scans-recipient"
              />
            </Form.Item>

            <Form.Item label="Сканы комплекта" required>
              <div
                style={{
                  maxHeight: 360,
                  overflow: 'auto',
                  border: '1px solid var(--border-color, #f0f0f0)',
                  borderRadius: 6,
                  padding: 8,
                  marginBottom: 12,
                }}
              >
                <ClosingPackageScanDocumentsTable projection={projection} />
              </div>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => void handleDownloadArchive()}
                loading={archiveDownloading}
                disabled={archiveEntryCount === 0}
                data-testid="send-scans-download-archive"
              >
                Скачать архив
              </Button>
              {archiveDownloaded ? (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginTop: 12 }}
                  message="Архив скачан — можно подтвердить отправку"
                />
              ) : null}
            </Form.Item>

            <Form.Item name="comment" label="Комментарий">
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder="Комментарий для журнала комплекта (необязательно)"
                data-testid="send-scans-comment"
              />
            </Form.Item>
          </Form>

          <ClosingPackageTaskModalEventDateHint />
        </>
      )}
    </Modal>
  );
};

export default ClosingPackageSendScansModal;
